import { DayOfWeek, LocalDate, LocalTime } from "@js-joda/core";
import { PatientTaskCustomizeDayTimeParamsTypes } from "./PatientTaskCustomizeDayTimeParams.types";
import immer from "immer";

export const allDaysOfWeek = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
];

function getDaysOfWeekInRange(
  dateRange: PatientTaskCustomizeDayTimeParamsTypes.DateRange
): Set<DayOfWeek> {
  const fixedEndDate = dateRange[1] ?? dateRange[0].plusWeeks(1);
  const daysOfWeek = new Set<DayOfWeek>();
  for (
    let day = dateRange[0];
    !day.isAfter(fixedEndDate) && daysOfWeek.size < 7;
    day = day.plusDays(1)
  ) {
    daysOfWeek.add(day.dayOfWeek());
  }
  return daysOfWeek;
}

function generateDefaultDayTimeParamsByDate(
  dateRange: PatientTaskCustomizeDayTimeParamsTypes.DateRange
): PatientTaskCustomizeDayTimeParamsTypes.DayTimeParamsType.SpecificDates {
  return {
    type: "SpecificDates",
    params: [
      {
        date: dateRange[0],
        timeSlots: [{ startTime: LocalTime.of(9), endTime: LocalTime.of(13) }],
      },
    ],
  };
}

function generateDefaultDayTimeParamsByDayOfWeek(
  dateRange: PatientTaskCustomizeDayTimeParamsTypes.DateRange
): PatientTaskCustomizeDayTimeParamsTypes.DayTimeParamsType.Pattern {
  const [startDate, dueDate] = dateRange;
  const defaultDayTimeParams: PatientTaskCustomizeDayTimeParamsTypes.DayTimeParams = {
    type: "Pattern",
    params: [],
  };

  // this need to iterate and generate the params for each day of the week between the start and end date
  // also need to check not to go over the same day of week twice
  const dueDateFixed = dueDate ?? startDate.plusMonths(1);
  for (let day = startDate; !day.isAfter(dueDateFixed); day = day.plusDays(1)) {
    const dayOfWeek = day.dayOfWeek();
    // 7 days populated
    if (defaultDayTimeParams.params.length === 7) {
      break;
    }
    if (defaultDayTimeParams.params.find((param) => param.dayOfWeek === dayOfWeek)) {
      continue;
    }

    defaultDayTimeParams.params.push({
      dayOfWeek: dayOfWeek,
      timeSlots: [
        {
          startTime: LocalTime.of(9),
          endTime: LocalTime.of(13),
        },
      ],
    });
  }

  return defaultDayTimeParams;
}

// When receiving the data from the server it does not parse it because it is coming from AngularJS
// and it is not serializing the data correctly
function parseDayTimeParamsIfNeeded(
  dayTimeScheduleOverrideParams:
    | PatientTaskCustomizeDayTimeParamsTypes.DayTimeParams
    | null
    | undefined
): PatientTaskCustomizeDayTimeParamsTypes.DayTimeParams | null | undefined {
  if (dayTimeScheduleOverrideParams === null || dayTimeScheduleOverrideParams === undefined) {
    return null;
  }

  switch (dayTimeScheduleOverrideParams.type) {
    case "SpecificDates":
      return immer(dayTimeScheduleOverrideParams, (draft) => {
        draft.params.forEach((param) => {
          if (typeof param.date === "string") {
            param.date = LocalDate.parse(param.date);
          }
          param.timeSlots.forEach((timeSlot) => {
            if (typeof timeSlot.startTime === "string") {
              timeSlot.startTime = LocalTime.parse(timeSlot.startTime);
            }
            if (typeof timeSlot.endTime === "string") {
              timeSlot.endTime = LocalTime.parse(timeSlot.endTime);
            }
          });
        });
      });
    case "Pattern":
      return immer(dayTimeScheduleOverrideParams, (draft) => {
        draft.params.forEach((param) => {
          if (typeof param.dayOfWeek === "string") {
            param.dayOfWeek = DayOfWeek.valueOf(param.dayOfWeek);
          }
          param.timeSlots.forEach((timeSlot) => {
            if (typeof timeSlot.startTime === "string") {
              timeSlot.startTime = LocalTime.parse(timeSlot.startTime);
            }
            if (typeof timeSlot.endTime === "string") {
              timeSlot.endTime = LocalTime.parse(timeSlot.endTime);
            }
          });
        });
      });
  }
}

export const patientTaskCustomizeDayTimeParamsUtils = {
  allDaysOfWeek,
  getDaysOfWeekInRange,
  generateDefaultDayTimeParamsByDate,
  generateDefaultDayTimeParamsByDayOfWeek,
  parseDayTimeParamsIfNeeded,
};
